import {
  Eye,
  Flame,
  CircleCheck,
  CircleSlash,
  type LucideIcon,
} from "lucide-react";
import { useTranslate } from "ra-core";

import { cn } from "@/lib/utils";

import {
  isTrackingStatus,
  TRACKING_STATUSES,
  trackingStatusLabelKey,
} from "./labels";
import { useTrackingStatusChange } from "./marketHooks";
import { QuickMenu } from "./QuickMenu";
import type { MarketCompetitor, TrackingStatus } from "./types";

const STATUS_STYLE: Record<
  TrackingStatus,
  { className: string; icon: LucideIcon }
> = {
  vigilar: {
    icon: Eye,
    className:
      "bg-sky-500/12 text-sky-800 ring-sky-500/25 dark:bg-sky-400/15 dark:text-sky-200 dark:ring-sky-400/30",
  },
  analizado: {
    icon: CircleCheck,
    className:
      "bg-emerald-500/12 text-emerald-800 ring-emerald-500/25 dark:bg-emerald-400/15 dark:text-emerald-200 dark:ring-emerald-400/30",
  },
  amenaza_alta: {
    icon: Flame,
    className:
      "bg-red-500/12 text-red-700 ring-red-500/25 dark:bg-red-400/15 dark:text-red-300 dark:ring-red-400/30",
  },
  descartado: {
    icon: CircleSlash,
    className:
      "bg-slate-500/10 text-slate-700 ring-slate-500/20 dark:bg-slate-300/12 dark:text-slate-300 dark:ring-slate-300/25",
  },
};

export const TrackingStatusBadge = ({
  status,
  className,
}: {
  status: TrackingStatus;
  className?: string;
}) => {
  const translate = useTranslate();
  const known = isTrackingStatus(status) ? status : "vigilar";
  const { icon: Icon, className: tone } = STATUS_STYLE[known];
  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ring-1 ring-inset",
        tone,
        className,
      )}
    >
      <Icon aria-hidden className="size-3.5" />
      <span className="sr-only">
        {translate("market.tracking_status.label")}:{" "}
      </span>
      {translate(trackingStatusLabelKey(known))}
    </span>
  );
};

/** The status badge as a menu to change it in one tap. */
export const TrackingStatusMenu = ({
  competitor,
}: {
  competitor: MarketCompetitor;
}) => {
  const translate = useTranslate();
  const change = useTrackingStatusChange();
  const status = competitor.tracking_status ?? "vigilar";
  return (
    <QuickMenu<TrackingStatus>
      label={`${translate("market.tracking_status.change")}: ${competitor.name}`}
      value={status}
      options={TRACKING_STATUSES.map((value) => ({
        value,
        label: translate(trackingStatusLabelKey(value)),
      }))}
      onChange={(value) => change(competitor, value)}
      trigger={<TrackingStatusBadge status={status} />}
    />
  );
};
